import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { 
  SlidersHorizontal, X, ChevronDown, Search, 
  Grid3X3, List, ArrowLeft 
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import ProductCard from '@/components/product/ProductCard';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Category = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, getLocalizedField } = useLanguage();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: slug === 'all' ? '' : slug,
    priceRange: [0, 50],
    brands: [],
    isEco: false,
    isOnSale: false,
  });

  const [sortBy, setSortBy] = useState('featured');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/brands`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setBrands(brandsRes.data);

        if (slug && slug !== 'all') {
          const cat = categoriesRes.data.find(c => c.slug === slug);
          setCurrentCategory(cat);
        } else {
          setCurrentCategory(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Update filters when URL changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchParams.get('search') || '',
      category: slug === 'all' ? '' : slug,
    }));
  }, [searchParams, slug]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Category filter
    if (filters.category) {
      const cat = categories.find(c => c.slug === filters.category);
      if (cat) {
        result = result.filter(p => p.category_id === cat.id);
      }
    }

    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(p =>
        p.name_en.toLowerCase().includes(search) ||
        p.name_es.toLowerCase().includes(search) ||
        p.brand.toLowerCase().includes(search)
      );
    }

    // Price filter
    result = result.filter(p => 
      p.price >= filters.priceRange[0] && p.price <= filters.priceRange[1]
    );

    // Brand filter
    if (filters.brands.length > 0) {
      result = result.filter(p => filters.brands.includes(p.brand));
    }

    // Eco filter
    if (filters.isEco) {
      result = result.filter(p => p.is_eco);
    }

    // On Sale filter
    if (filters.isOnSale) {
      result = result.filter(p => p.is_on_sale);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'featured':
      default:
        result.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
    }

    return result;
  }, [products, categories, filters, sortBy]);

  const clearFilters = () => {
    setFilters({
      search: '',
      category: slug === 'all' ? '' : slug,
      priceRange: [0, 50],
      brands: [],
      isEco: false,
      isOnSale: false,
    });
    setSearchParams({});
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 50) count++;
    if (filters.brands.length > 0) count++;
    if (filters.isEco) count++;
    if (filters.isOnSale) count++;
    return count;
  }, [filters]);

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      {slug === 'all' && (
        <div>
          <h4 className="font-semibold text-slate-900 mb-3">{t('nav.categories')}</h4>
          <div className="space-y-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => navigate(`/category/${cat.slug}`)}
                className={`w-full text-left px-3 py-2 rounded-xl transition-colors ${
                  filters.category === cat.slug
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-slate-100 text-slate-600'
                }`}
              >
                {getLocalizedField(cat, 'name')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Price Range */}
      <div>
        <h4 className="font-semibold text-slate-900 mb-3">{t('products.price')}</h4>
        <div className="px-2">
          <Slider
            value={filters.priceRange}
            onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
            max={50}
            step={1}
            className="mb-4"
          />
          <div className="flex justify-between text-sm text-slate-600">
            <span>${filters.priceRange[0]}</span>
            <span>${filters.priceRange[1]}</span>
          </div>
        </div>
      </div>

      {/* Brands */}
      <div>
        <h4 className="font-semibold text-slate-900 mb-3">{t('products.brand')}</h4>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {brands.map((brand) => (
            <label key={brand} className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={filters.brands.includes(brand)}
                onCheckedChange={(checked) => {
                  setFilters(prev => ({
                    ...prev,
                    brands: checked
                      ? [...prev.brands, brand]
                      : prev.brands.filter(b => b !== brand)
                  }));
                }}
              />
              <span className="text-slate-600">{brand}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Eco-friendly */}
      <label className="flex items-center gap-3 cursor-pointer">
        <Checkbox
          checked={filters.isEco}
          onCheckedChange={(checked) => setFilters(prev => ({ ...prev, isEco: checked }))}
        />
        <span className="text-slate-600">{t('products.eco')}</span>
      </label>

      {/* On Sale */}
      <label className="flex items-center gap-3 cursor-pointer">
        <Checkbox
          checked={filters.isOnSale}
          onCheckedChange={(checked) => setFilters(prev => ({ ...prev, isOnSale: checked }))}
        />
        <span className="text-slate-600">{t('products.onSale')}</span>
      </label>

      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full"
        >
          <X className="w-4 h-4 mr-2" />
          {t('products.clearFilters')}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="category-page">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <button 
                onClick={() => navigate('/')} 
                className="flex items-center gap-1 hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('nav.home')}
              </button>
              <span>/</span>
              <span className="text-slate-900">
                {currentCategory ? getLocalizedField(currentCategory, 'name') : t('products.all')}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                  {currentCategory ? getLocalizedField(currentCategory, 'name') : t('products.all')}
                </h1>
                <p className="text-slate-500 mt-1">
                  {filteredProducts.length} {t('categories.products')}
                </p>
              </div>

              {/* Search in category */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder={t('hero.search')}
                  className="w-full pl-10 pr-4 h-12 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  data-testid="category-search-input"
                />
              </div>
            </div>
          </motion.div>

          <div className="flex gap-8">
            {/* Desktop Filters Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden lg:block w-64 flex-shrink-0"
            >
              <div className="sticky top-28 card-glass p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5" />
                  {t('products.filters')}
                  {activeFiltersCount > 0 && (
                    <span className="ml-auto w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </h3>
                <FilterContent />
              </div>
            </motion.aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between gap-4 mb-6 p-4 card-glass"
              >
                {/* Mobile Filter Button */}
                <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden relative">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      {t('products.filters')}
                      {activeFiltersCount > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                          {activeFiltersCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <SheetHeader>
                      <SheetTitle>{t('products.filters')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <div className="flex items-center gap-3 ml-auto">
                  <span className="hidden sm:block text-sm text-slate-500">{t('products.sortBy')}:</span>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40" data-testid="sort-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="price-low">Price: Low to High</SelectItem>
                      <SelectItem value="price-high">Price: High to Low</SelectItem>
                      <SelectItem value="rating">Rating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* View Mode */}
                <div className="hidden sm:flex items-center gap-1 p-1 rounded-lg bg-slate-100">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'
                    }`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-slate-200'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>

              {/* Products Grid */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(9)].map((_, i) => (
                    <Skeleton key={i} className="h-96 rounded-3xl" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <p className="text-xl text-slate-500">{t('products.noProducts')}</p>
                  <Button onClick={clearFilters} className="mt-4">
                    {t('products.clearFilters')}
                  </Button>
                </motion.div>
              ) : (
                <LayoutGroup>
                  <motion.div
                    layout
                    className={`grid gap-6 ${
                      viewMode === 'grid'
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                        : 'grid-cols-1'
                    }`}
                  >
                    <AnimatePresence mode="popLayout">
                      {filteredProducts.map((product) => (
                        <motion.div
                          key={product.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ProductCard product={product} />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </LayoutGroup>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Category;
